import { cn } from "@repo/ui/lib/utils"
import * as React from "react"

interface InputProps extends React.ComponentProps<"input"> {
  suffix?: string
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    { className, type, suffix, value, onChange, onFocus, onBlur, ...props },
    ref,
  ) => {
    const [displayValue, setDisplayValue] = React.useState(() => {
      return value?.toString() ?? ""
    })
    const [isFocused, setIsFocused] = React.useState(false)

    // Update display value when value prop changes.
    // For number inputs, skip while focused so the user can clear "0" without it snapping back.
    React.useEffect(() => {
      if (type === "number" && isFocused) return
      setDisplayValue(value?.toString() ?? "")
    }, [value, isFocused, type])

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true)

      // If it's a number input and the value is 0, clear it when focused
      if (type === "number" && (value === 0 || value === "0")) {
        setDisplayValue("")
      }

      if (onFocus) {
        onFocus(e)
      }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      setDisplayValue(newValue)

      if (onChange) {
        // For number inputs, only call onChange if the value is not empty
        // This prevents automatic conversion of empty string to 0
        if (type === "number" && newValue === "") {
          // Don't call onChange for empty values during typing
          return
        }
        onChange(e)
      }
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false)

      if (type === "number" && !e.target.value) {
        // If it's a number input and empty on blur, set to 0
        setDisplayValue("0")

        // Create a synthetic event with value "0" to trigger form updates
        const syntheticEvent = {
          ...e,
          target: { ...e.target, value: "0" },
        } as React.ChangeEvent<HTMLInputElement>

        if (onChange) {
          onChange(syntheticEvent)
        }
      }

      if (onBlur) {
        onBlur(e)
      }
    }

    const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
      // Prevent scroll from changing the value for number inputs
      if (type === "number") {
        e.currentTarget.blur()
      }
    }

    return (
      <div className="relative w-full">
        <input
          data-hj-allow={type !== "password"}
          type={type}
          className={cn(
            "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
            suffix && "pl-2 pr-5",
            className,
          )}
          ref={ref}
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onWheel={handleWheel}
          {...props}
        />
        {suffix && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
            {suffix}
          </span>
        )}
      </div>
    )
  },
)
Input.displayName = "Input"

export { Input }
